import React, { Suspense, useEffect, useMemo, useState, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MobileTopBar } from './components/MobileTopBar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { MobileFab } from './components/MobileFab';
import { SearchOverlay } from './components/SearchOverlay';
import { MobileNotifications } from './components/MobileNotifications';
import { cachedGet, invalidate } from './lib/cache';

const DashboardTab = lazy(() => import('./tabs/DashboardTab'));
const LeadsTab = lazy(() => import('./tabs/LeadsTab'));
const TripsTab = lazy(() => import('./tabs/TripsTab'));
const TravellersTab = lazy(() => import('./tabs/TravellersTab'));
const SettingsTab = lazy(() => import('./tabs/SettingsTab'));

// 'leads' stays a renderable tab (reached via Dashboard chips / FAB / search)
// but is NOT in the bottom nav — Travellers replaced it.
const VALID = ['dashboard', 'leads', 'trips', 'travellers', 'settings'];
const Spinner = () => <div className="flex justify-center pt-16"><Loader2 className="h-6 w-6 animate-spin text-[#0F6E56]" /></div>;

/**
 * MobileTabShell — the home of the mobile app. Holds the tabs keep-alive
 * (lazy-mounted on first visit, kept mounted after) so tab switching is
 * instant with preserved scroll/state. Top bar + bottom nav stay fixed.
 *
 * FIX 3 (Task 3) — the URL is the single source of truth for the active tab
 * (`?tab=<key>`). The active tab is DERIVED from the URL, never from local
 * component state, so back / refresh / bookmark always open the right section.
 */
export const MobileTabShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const active = VALID.includes(tabParam) ? tabParam : 'dashboard';

  const [visited, setVisited] = useState(() => new Set([active]));
  const [tripFilter, setTripFilter] = useState('all');
  const [leadFilter, setLeadFilter] = useState('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [badge, setBadge] = useState(0);

  // Keep visited tabs mounted (keep-alive). Derived from the URL-driven active.
  useEffect(() => {
    setVisited((s) => (s.has(active) ? s : new Set(s).add(active)));
  }, [active]);

  useEffect(() => {
    cachedGet('/api/notifications', { force: true }).then((d) => setBadge(Number(d?.actionable || 0))).catch(() => {});
  }, [refreshKey]);

  const goTab = (key, opts = {}) => {
    if (!VALID.includes(key)) return;
    if (opts.filter) setTripFilter(opts.filter);
    if (opts.leadFilter) setLeadFilter(opts.leadFilter);
    // URL is authoritative — push the tab into the query string.
    navigate(`/?tab=${key}`);
  };

  const renderTab = (key) => {
    switch (key) {
      case 'dashboard': return <DashboardTab goTab={goTab} refreshKey={refreshKey} />;
      case 'leads': return <LeadsTab refreshKey={refreshKey} initialFilter={leadFilter} />;
      case 'trips': return <TripsTab refreshKey={refreshKey} initialFilter={tripFilter} />;
      case 'travellers': return <TravellersTab refreshKey={refreshKey} />;
      case 'settings': return <SettingsTab />;
      default: return null;
    }
  };

  const showFab = active === 'dashboard' || active === 'trips' || active === 'leads';
  const tabs = useMemo(() => VALID, []);

  return (
    <div className="fixed inset-0 bg-[#F8F9FA]" data-testid="mobile-app-shell">
      <MobileTopBar onSearch={() => setSearchOpen(true)} onBell={() => setNotifOpen(true)} badge={badge} />

      <main className="absolute inset-x-0 top-[52px]" style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }}>
        {tabs.map((key) => visited.has(key) && (
          <div key={key} className="absolute inset-0 overflow-y-auto overscroll-contain" style={{ display: active === key ? 'block' : 'none' }} data-testid={`m-tabpane-${key}`}>
            <Suspense fallback={<Spinner />}>{renderTab(key)}</Suspense>
          </div>
        ))}
      </main>

      <MobileBottomNav active={active} onChange={(k) => goTab(k)} />
      {showFab && <MobileFab onLeadCreated={() => setRefreshKey((k) => k + 1)} />}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <MobileNotifications open={notifOpen} onClose={() => setNotifOpen(false)} onRead={() => { setBadge(0); invalidate('/api/notifications'); }} />
    </div>
  );
};

export default MobileTabShell;
